import { supabase } from '@/integrations/supabase/client';
import { isStandaloneMode } from '@/utils/platform';

// Funil de entrada antes de existir conta (23/09/2026).
//
// app_events só aceita quem já entrou, então as telas de cadastro e login eram cegas:
// sabíamos quantas compradoras não criavam conta, nunca onde travavam. Esta é a única
// porta de escrita para public.auth_funnel_events (migration 20260923232648), que aceita
// insert anônimo e só o admin lê. Contrato: .claude/blueprints/2026-09-23-medicao-do-login.md
//
// Regras que a tabela também impõe: nenhum email, nenhum texto digitado, nenhuma mensagem
// de erro crua. Tela e passo vêm de listas fechadas; `detail` é um rótulo curto.
// Dispara e esquece: nunca um `await` no caminho de entrar, nunca um erro na tela.

export type AuthScreen = 'welcome_hp' | 'auth' | 'forgot';
export type AuthStep =
  | 'shown' | 'form_started' | 'submit' | 'rate_limited' | 'validation_error'
  | 'signin_ok' | 'signup_ok' | 'account_exists' | 'password_mismatch' | 'email_not_approved'
  | 'network_error' | 'other_error' | 'exception' | 'already_signed_in'
  | 'opened' | 'code_sent' | 'code_send_failed' | 'code_ok' | 'code_bad' | 'password_set';

const ANON_KEY = 'auth_funnel_anon_id';

// crypto.randomUUID só existe no iOS 15.4+; aparelho antigo é justamente deste público.
function newUuid(): string {
  if (typeof crypto !== 'undefined' && typeof crypto.randomUUID === 'function') return crypto.randomUUID();
  return 'xxxxxxxx-xxxx-4xxx-8xxx-xxxxxxxxxxxx'.replace(/x/g, () => Math.floor(Math.random() * 16).toString(16));
}

// Sem storage (aba privada antiga), o id vive na memória: um por carregamento, e não um
// por evento (achado da revisão do Astra em 23/09: três eventos viravam três pessoas).
let memoryAnonId: string | null = null;

function getAnonId(): string {
  try {
    const existing = localStorage.getItem(ANON_KEY);
    if (existing) return existing;
    const fresh = memoryAnonId ?? newUuid();
    localStorage.setItem(ANON_KEY, fresh);
    return fresh;
  } catch {
    memoryAnonId = memoryAnonId ?? newUuid();
    return memoryAnonId;
  }
}

/**
 * Só passa rótulo que JÁ está no formato de código (letras minúsculas, números, sublinhado).
 * Nada é transformado antes de checar: a versão anterior trocava o '@' por '_' e depois
 * procurava '@', então um email passaria como "mary_example.com" (revisão do Astra, 23/09).
 * O banco impõe a mesma regra (migration 20260923235931).
 */
export function cleanDetail(detail?: string): string | null {
  if (!detail) return null;
  return /^[a-z][a-z0-9_]{0,59}$/.test(detail) ? detail : 'unrecognized';
}

export function trackAuthStep(screen: AuthScreen, step: AuthStep, opts: { detail?: string; userId?: string } = {}): void {
  try {
    void supabase
      .from('auth_funnel_events' as never)
      .insert({
        anon_id: getAnonId(),
        user_id: opts.userId ?? null,
        screen,
        step,
        detail: cleanDetail(opts.detail),
        is_standalone: isStandaloneMode(),
      } as never)
      .then(() => undefined, () => undefined);
  } catch {
    // Medição nunca derruba a entrada.
  }
}
